import Box from '@mui/material/Box';
import { device, handset, radius } from '@platform/tokens';

import type { ReactNode } from 'react';

/**
 * The viewer's URL carrying the drawn phone's safe-area insets, which a browser in an
 * iframe reports as 0 — LESSON-25. The viewer reads them in `usePayload.ts`.
 */
export function framedViewerUrl(viewerUrl: string) {
  const url = new URL(viewerUrl);
  url.searchParams.set('insetTop', String(handset.insets.top));
  url.searchParams.set('insetBottom', String(handset.insets.bottom));
  return url.toString();
}

/**
 * The phone the preview lives inside — GEN-80.
 *
 * A rounded box read as a card in the console rather than as a screen on a device, and
 * that began to matter once the preview became playable: *how much is above the fold* is
 * the question this pane answers, and a card has no fold.
 *
 * Drawn rather than an image, and every measurement is `handset` in `packages/tokens`,
 * beside the screen size it is derived from.
 */
export function DeviceFrame({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        padding: `${String(handset.bezel)}px`,
        borderRadius: `${String(radius['2xl'] + handset.bezel)}px`,
        bgcolor: 'grey.900',
        boxShadow: 6,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: `${String(device.width)}px`,
          height: `${String(device.height)}px`,
          borderRadius: `${String(radius['2xl'])}px`,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {children}

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: `${String(handset.island.top)}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            width: `${String(handset.island.width)}px`,
            height: `${String(handset.island.height)}px`,
            borderRadius: `${String(handset.island.height / 2)}px`,
            bgcolor: 'common.black',
            pointerEvents: 'none',
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: `${String(handset.indicator.bottom)}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            width: `${String(handset.indicator.width)}px`,
            height: `${String(handset.indicator.height)}px`,
            borderRadius: `${String(handset.indicator.height / 2)}px`,
            bgcolor: 'common.black',
            opacity: 0.35,
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
}
